/**
 * 自由出行 · 满屏小地图（周末模型 2026-10-11，设计基准 docs/自由出行与小地图-设计方案.md §三/§八）
 *
 * 形态：MonthProgressPanel 周末阶段选「出门」后弹出的全屏覆盖层（modal 体系），内含三屏：
 *   map   = 满屏地图（web/public/freeroam/map-base.webp，9:19.5 cover 铺满）+ 三栋建筑 pin
 *           （坐标=茄子拖拽定稿，图像空间百分比，锚点=pin 顶部 translate(-50%,-100%)）；
 *   street= 满屏商业街（b-street.webp）+ 六家店纯按钮（坐标同上拖拽定稿）；
 *   filmcity = 探班（垫 scenes/bg-filmcity.webp，名单=在拍剧组，先选组再选组里见谁，2026-10-12 茄子令）。
 * 行动点 1 点（2026-10-11 拍板）：点一栋建筑/一家店所在屏即花掉本周末唯一的 1 点。
 * 底部出口钮＝一颗钮一个位置，文案与显隐由「这屏还有没有该做的事」算出（2026-10-11 茄子当场改判：
 *   「我本来也没有要求这个按钮常驻，应该是 AI 自己写的」⇒ 只在需要时浮现。原「常驻唯一周出口」出自
 *   3af3611 写进设计方案 §186 的「✅ 茄子拍板」一条，查无茄子原话，本批按改判作废）。
 *   2026-10-12 茄子令「周末就直接进入地图，然后在底部加一个在家休息的选项」＋「行动点消耗完之后点击
 *   离开商业街应该直接推进下一周，现在是回到地图但也进入不了任何地点」⇒ 二选一卡退役，本组件就是周末入口，
 *   三档：地图屏=「在家休息」（+10 体力那条账，不许出门）、街景=「离开商业街」、影视城=「离开影视城」，
 *   后两者即收口（那两屏点数恒为 0，"回地图"从此没有下一站 ⇒ 原「结束出行」档与 `.glow` 成死档已删）。
 *   店面全屏与 VN 演出期=隐藏。不许靠点屏推进。
 * 随机事件不许进周末（照出行周口径）——本组件不掷 eventPending，掷了也不消费（守卫在宿主 MonthProgressPanel）。
 * 偶遇/探班/兼职旁白用组件自带的 VnDialog（在覆盖层内，不许与宿主的常驻叙事卡抢卡；
 *   赴约不在这里演——选周自动化后归 MonthProgressPanel，见 FreeroamSystem 头注释）。
 * 店面（四家部件店 + 礼品店）在 `FreeroamShops.ts`（类目过滤 / 价签购买 / 买断即上身 / 钱不够置灰可点）；
 * 本组件只管屏与坐标，落档一律经 `GameState.wearDraft`（不许直写 player.avatar、不许调 ensureOwned）。
 */
import type { GameApp } from '../app';
import { GameState } from '../systems/GameState';
import { freeroamSystem } from '../systems/FreeroamSystem';
import { encounterSystem, EncounterOffer, EncounterOutcome } from '../systems/EncounterSystem';
import { PlotSystem } from '../systems/PlotSystem';
import { socialSystem } from '../systems/SocialSystem';
import { scriptSystem } from '../systems/ScriptSystem';
import { GameData } from '../data/GameData';
import { LOCATION_MAP, LocationId } from '../data/tables/Locations';
import { UNREACHABLE_LINES } from '../data/tables/EncounterTexts';
import { DialogLine, DialogSpeaker, VnDialog } from './VnDialog';
import { PlotPlayer } from './plotPlayback';
import { createButton, createDiv, createSpan, showHint } from './ui';
import { filledIcon, FilledIconName } from './icons';
import { PartShop, GiftShop, SHOP_CATS, ShopHandle } from './FreeroamShops';
import { withSpeakerAvatar } from './npcAvatar';
import { MediaPanel } from './panels/MediaPanel';
import { applyLqip, clearLqip } from '../data/SceneBgs';

/**
 * 地图三 pin（图像空间 %，锚点与样张一致=translate(-50%,-88%)）。
 * ⚠️ 纵坐标已按底图裁切换算：样张原底图 1344×2912，正式 webp 裁掉底部 111px（=2801），
 * 样张 y% × (2912/2801) = 游戏 y%（x 不变）。样张 map 屏已换用同一张裁后底图，此后拖拽导出即游戏真实坐标。
 */
const MAP_PINS: { loc: LocationId; x: number; y: number; img: string }[] = [
    { loc: 'loc_filmcity', x: 24.0, y: 26.7, img: 'freeroam/b-filmcity.webp' },
    { loc: 'loc_park', x: 37.5, y: 42.9, img: 'freeroam/b-park.webp' },
    { loc: 'loc_street', x: 48.9, y: 58.5, img: 'freeroam/b-streetpin.webp' },
];

/** 商业街六店按钮（坐标=样张拖拽定稿，图像空间 %；图标=样张同款 MingCute/Phosphor 实心系） */
const STREET_SHOPS: { id: LocationId; x: number; y: number; icon: FilledIconName }[] = [
    { id: 'shop_cloth', x: 16.0, y: 32.5, icon: 'shirt' },
    { id: 'shop_hair', x: 57.6, y: 43.6, icon: 'scissorsFill' },
    { id: 'shop_gift', x: 46.7, y: 56.2, icon: 'shoppingBag' },
    { id: 'shop_cafe', x: 18.7, y: 64.0, icon: 'coffeeFill' },
    { id: 'shop_acc', x: 73.9, y: 51.7, icon: 'diamondFill' },
    { id: 'shop_derma', x: 74.9, y: 35.8, icon: 'sparklesFill' },
];

type FrScreen = 'map' | 'street' | 'filmcity';

export class FreeroamMap {
    readonly element: HTMLElement;
    private readonly vn = new VnDialog();
    private readonly mediaPanel = new MediaPanel();
    /**
     * 本地图的剧情演出档（执行宿主，与月度执行页共用 `PlotPlayer`——2026-10-14 抽公共）。
     * 三张嘴：VN 用本地图的常驻覆盖卡（垫 `plotScene` 那张地点图）、media 卡弹本地图的 MediaPanel、
     * 演完走 `endVisit(plotLanding)`（公园收口回地图、咖啡馆收口回街景）。
     * `plotScene`/`plotLanding` 由 `playPlotBeat` 的调用方给（一次访问内不变，跨 media/VN 回调要复用）。
     */
    private readonly plotPlayer = new PlotPlayer('execution', {
        playLines: (lines, onEnd) => this.playVn(lines, this.plotScene, onEnd),
        showMedia: media => this.mediaPanel.show(media),
        onDrained: () => this.endVisit(this.plotLanding),
    });
    /**
     * 本次访问的地点底图（VN 演出垫底用）。**无默认值**——2026-10-30 修：原默认值
     * `scenes/bg-street.webp` 在 `public/scenes/` 下并不存在（只有 bg-park/bg-cafe/bg-filmcity 等），
     * 一旦某条路径在 `playPlotBeat` 赋值前就演出，会静默 404 白底。现在未赋值＝不垫图（`playVn` 的 scene 可选）。
     */
    private plotScene: string | undefined;
    private plotLanding: 'street' | 'map' = 'street';
    // 行动点是业务账，记在 FreeroamSystem（2026-10-12 B 批从本组件搬走）
    private onEnd: (stayedHome: boolean) => void = () => { };
    private screen: FrScreen = 'map';
    /** 影视城展开的剧组（手风琴：同时只开一组；进屏即收起） */
    private openCrew: string | null = null;
    /** 待事角标两颗（地图屏商业街 pin / 街景屏咖啡馆店招），同一个判据见 `updatePendingDots` */
    private streetDot: HTMLElement | null = null;
    private cafeDot: HTMLElement | null = null;

    private readonly mapWrap: HTMLElement;
    private readonly streetWrap: HTMLElement;
    private readonly filmWrap: HTMLElement;
    private onRefresh: (() => void) | null = null;
    /** 顶栏数值刷新（逛街花钱后；宿主注入，缺省空操作） */
    private refreshHost(): void { this.onRefresh?.(); }
    private readonly sceneEl: HTMLElement;
    private readonly sceneImg: HTMLImageElement;
    /** 店面组件（懒建，同一时刻只开一家；DOM 首次打开时挂进覆盖层） */
    private openShop: ShopHandle | null = null;
    private readonly partShops: Partial<Record<string, PartShop>> = {};
    private giftShop: GiftShop | null = null;
    private readonly endBtn: HTMLButtonElement;

    constructor(private readonly app: GameApp) {
        const root = createDiv('fr-overlay');
        root.addEventListener('click', e => e.stopPropagation());   // 覆盖层不许冒泡（宿主面板的 tryAdvance 会把点击当推进）

        // --- 地图屏 ---
        this.mapWrap = createDiv('fr-screen');
        const mapBgwrap = createDiv('fr-bgwrap');
        const mapImg = createImg('freeroam/map-base.webp');
        mapBgwrap.appendChild(mapImg);
        bindBgLqip(mapBgwrap, mapImg, 'freeroam/map-base.webp');
        MAP_PINS.forEach(pin => {
            const el = createDiv('fr-pin');
            el.style.left = `${pin.x}%`;
            el.style.top = `${pin.y}%`;
            // 锚点=样张 .pin.bld 同款 -88%（别用 -100%：会让 pin 比样张低一截，相对位置漂移）
            const img = createImg(pin.img, 'fr-pin-img');
            el.appendChild(img);
            const nameEl = createSpan('fr-pin-name', locName(pin.loc));
            if (pin.loc === 'loc_street') this.streetDot = nameEl.appendChild(pendingDot());
            el.appendChild(nameEl);
            el.addEventListener('click', e => { e.stopPropagation(); this.tryGo(pin.loc); });
            mapBgwrap.appendChild(el);
        });
        this.mapWrap.appendChild(mapBgwrap);
        this.mapWrap.dataset.bg = '1';

        // --- 商业街屏 ---
        this.streetWrap = createDiv('fr-screen');
        const streetBgwrap = createDiv('fr-bgwrap');
        const streetImg = createImg('freeroam/b-street.webp');
        streetBgwrap.appendChild(streetImg);
        bindBgLqip(streetBgwrap, streetImg, 'freeroam/b-street.webp');
        STREET_SHOPS.forEach(shop => {
            // 样张同款结构：纸卡横排签（实心图标+店名）在上，引线+圆点脚在下，锚点=-100%（圆点落在坐标上）
            const el = createDiv('fr-shop-btn');
            el.style.left = `${shop.x}%`;
            el.style.top = `${shop.y}%`;
            const card = createDiv('fr-shop-pill');
            card.appendChild(filledIcon(shop.icon, 0.85));
            card.appendChild(createSpan('nm', locName(shop.id)));
            if (shop.id === 'shop_cafe') this.cafeDot = card.appendChild(pendingDot());
            el.appendChild(card);
            el.appendChild(createDiv('fr-stem'));
            el.appendChild(createDiv('fr-pfoot'));
            el.addEventListener('click', e => { e.stopPropagation(); this.enterShop(shop.id); });
            // 按压反馈用 JS 类驱动（:active 在触屏上缺 touch 监听不触发；抬手/划出即回弹）
            el.addEventListener('pointerdown', () => el.classList.add('pressing'));
            for (const ev of ['pointerup', 'pointerleave', 'pointercancel']) {
                el.addEventListener(ev, () => el.classList.remove('pressing'));
            }
            streetBgwrap.appendChild(el);
        });
        this.streetWrap.appendChild(streetBgwrap);
        this.streetWrap.dataset.bg = '1';
        // 屏内不许再放自己的出口钮（丙案）：街景的出口由共用底部钮承担（文案「离开商业街」）

        // --- 影视城屏 ---
        this.filmWrap = createDiv('fr-screen');
        const filmBgwrap = createDiv('fr-bgwrap');
        const filmImg = createImg('scenes/bg-filmcity.webp');
        filmBgwrap.appendChild(filmImg);
        bindBgLqip(filmBgwrap, filmImg, 'scenes/bg-filmcity.webp');
        this.filmWrap.appendChild(filmBgwrap);
        this.filmWrap.dataset.bg = '1';
        const filmHead = createDiv('fr-film-head');
        filmHead.appendChild(createSpan('fr-film-title', `${locName('loc_filmcity')} · 探班`));
        filmHead.appendChild(createSpan('fr-film-sub', '正在拍戏的剧组 · 有你认识的人才能探'));
        this.filmWrap.appendChild(filmHead);
        const filmList = createDiv('fr-film-list');
        filmList.dataset.role = 'filmList';
        this.filmWrap.appendChild(filmList);

        // --- 店面（四家部件店 + 礼品店）：FreeroamShops 组件，进街点店招时懒建并挂载 ---

        // --- 地点场景层（VN 播放期的全屏背景：公园=林荫长椅 / 咖啡馆=靠窗双人座；探班用影视城屏自带底图） ---
        const sceneEl = createDiv('fr-scene');
        const sceneImg = document.createElement('img');
        sceneImg.alt = '';
        sceneEl.appendChild(sceneImg);
        root.appendChild(sceneEl);
        this.sceneEl = sceneEl;
        this.sceneImg = sceneImg;

        // --- 底部出口钮（全屏共用一颗，文案/显隐见 updateFooter；2026-10-11 茄子令删「第X周·周末‖行动点」胶囊） ---
        this.endBtn = createButton('fr-end', '在家休息', () => this.onFooter());
        root.appendChild(this.endBtn);

        root.appendChild(this.mapWrap);
        root.appendChild(this.streetWrap);
        root.appendChild(this.filmWrap);
        root.appendChild(this.vn.element);
        // media 弹窗挂外壳、不挂本覆盖层（2026-10-16 弹窗统一批）：`.fr-overlay` 长在宿主面板根里，
        // 底部导航是它的兄弟节点、DOM 靠后，挂在层内就压不住那排按钮（决策页/月度页同因，三处一并改）
        this.app.overlayRoot.appendChild(this.mediaPanel.element);
        this.mediaPanel.onDone = () => this.onMediaDone();

        this.element = root;
        window.addEventListener('resize', this.fitAll);
        // ⚠️ 背景图加载完成前 naturalWidth=0，fitAll 会量空跳过（首屏白屏事故）——load 后补算；
        // 缓存命中时 load 已放过，complete 的当场补一次
        root.querySelectorAll<HTMLImageElement>('.fr-bg').forEach(img => {
            img.addEventListener('load', this.fitAll);
            if (img.complete && img.naturalWidth) this.fitAll();
        });
    }

    // ===== 生命周期 =====

    /** 周末开始：地图直接铺开（2026-10-12 茄子令撤二选一卡，"出不出门"这个岔口搬到底部那颗钮上）。
     *  行动点重置、屏切地图、fitWraps（⚠️ 隐藏屏 clientWidth=0，必须先显再量）。
     *  `onEnd(stayedHome)`：收口时告诉宿主玩家点的是不是「在家休息」——宅家那句旁白归宿主播。 */
    open(onEnd: (stayedHome: boolean) => void, onRefresh?: () => void): void {
        this.onEnd = onEnd;
        this.onRefresh = onRefresh ?? null;   // 逛街花钱/探班/兼职后让宿主刷新顶栏（茄子 10-11 令：保留信息栏+属性抽屉）
        freeroamSystem.startOuting();
        this.screen = 'map';
        this.resetTransient();
        this.element.classList.add('show');
        this.showScreen('map');
        requestAnimationFrame(() => this.fitAll());
    }

    /** 收口本周末并下屏（`stayedHome`=玩家点的是「在家休息」，体力账已在 `weekendStayHome` 结清） */
    private finish(stayedHome: boolean): void {
        if (!stayedHome) freeroamSystem.endOuting();
        // ⚠️ 收口钮在顶栏（z 高于场景层），VN 播放中也点得到——必须复位场景层与 VN 卡，
        // 不许留着它们出屏（实机首夜：上次 VN 开着直接结束出行，下次出行被公园场景层糊住、pin 全点不着）
        this.resetTransient();
        this.element.classList.remove('show');
        this.onEnd(stayedHome);
    }

    /** 收掉所有跨屏残留（VN 卡/场景层/商店弹卡/出口钮）：开屏与收口各调一次 */
    private resetTransient(): void {
        this.vn.stop();
        this.sceneEl.classList.remove('show');
        this.element.classList.remove('playing');   // 不许留着下次出行：三屏会被这颗态类摘掉点击
        this.closeShop();                           // 内部会 updateFooter()，故收钮必须排在它后面
        this.endBtn.classList.remove('show');
    }

    destroy(): void {
        window.removeEventListener('resize', this.fitAll);
        this.vn.destroy();
    }

    // ===== 屏切换 / 定位 =====

    private showScreen(s: FrScreen): void {
        this.screen = s;
        this.mapWrap.classList.toggle('active', s === 'map');
        this.streetWrap.classList.toggle('active', s === 'street');
        this.filmWrap.classList.toggle('active', s === 'filmcity');
        if (s === 'filmcity') { this.openCrew = null; this.renderFilmList(); }
        this.updateFooter();
        this.updatePendingDots();
        // ⚠️ 切屏后必须重算 fitWraps——隐藏屏 clientWidth=0 会量出 0×0（样张商业街白屏事故）
        requestAnimationFrame(() => this.fitAll());
    }

    /** 把当前屏的 bgwrap 按 cover 矩形包住背景（pin 按图像空间百分比挂层上，任何屏比跟地形） */
    private fitAll = (): void => {
        const screenEl = this.screen === 'map' ? this.mapWrap
            : this.screen === 'street' ? this.streetWrap : this.filmWrap;
        const wrap = screenEl.querySelector<HTMLElement>('.fr-bgwrap');
        const img = wrap?.querySelector('img');
        if (!wrap || !img || !img.naturalWidth) return;
        const cw = screenEl.clientWidth, ch = screenEl.clientHeight;
        if (!cw || !ch) return;
        const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
        const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
        wrap.style.width = `${w}px`;
        wrap.style.height = `${h}px`;
        wrap.style.left = `${(cw - w) / 2}px`;
        wrap.style.top = `${(ch - h) / 2}px`;
        // 矩形写好才许 pin 落位（未就位那一态见 `ui.css` 的 `.fr-bgwrap:not(.fitted)`）
        wrap.classList.add('fitted');
    };

    /**
     * 底部出口钮的唯一写点（丙案 2026-10-11；2026-10-12 茄子令「周末直接进地图」后四档塌三档）：
     * 文案与显隐都从「这一屏还有没有该做的事」推出来，屏内元素不许各自摆自己的出口钮。
     * 地图屏=「在家休息」（这周哪儿也不去，走 +10 体力那条账）；街景/影视城=「离开商业街 / 离开影视城」——
     * 那两屏的点数恒为 0（进屏即花），所以「离开」就是收口，不许再退回那张进不去任何地点的地图
     * （原「结束出行」那一档与它的 `.glow` 随这条路一起成死档，同日删）。
     * 店面全屏与 VN 演出期=这屏的事没做完，钮收起。
     */
    private updateFooter(): void {
        const busy = !!this.openShop || this.vn.open;
        // 演出期把三屏整层摘掉点击（场景层是纯背景，盖得住画盖不住手：实测名单行能透过片场底被点到）
        this.element.classList.toggle('playing', this.vn.open);
        this.endBtn.classList.toggle('show', !busy);
        if (busy) return;
        this.endBtn.textContent = this.screen === 'street' ? '离开商业街'
            : this.screen === 'filmcity' ? '离开影视城' : '在家休息';
    }

    /**
     * 「这地点这周末有事」那一枚红点（2026-10-12 茄子改判：在**有内容那个地点的入口**上挂提醒；
     * 只加读数，不许改任何目的地的可点性）。
     * 2026-10-28 起判据只剩一条＝该地点此刻有可派发的特殊剧情（赴约红点随选周自动化撤销——
     * 到点自动演出，玩家没有「该去哪儿赴约」要被提醒了；「宅家=作废」那句提醒同因消失）。
     * 画法＝无字圆点（14px 含 2px 纸色描边，2026-10-13 定）。
     * 落点跟着内容走：现在唯一有剧情的地点是拾光咖啡馆，所以只有它和通往它的商业街 pin 挂点。
     * 公园那侧不许挂。公园是 50% 偶遇掷点（`EncounterSystem.rollPark`，2026-10-28 起；池=15 名普通 NPC
     * 加可派发特殊 NPC），没掷中就是散步休息——去了不一定有人，与红点"来了就有"的承诺强度不符；
     * 店面/剧情口变多以后，判据要按各自店面分算，不许再全条共用一把。
     */
    private updatePendingDots(): void {
        const on = !!PlotSystem.getInstance().meetEventAtLocation('shop_cafe');
        this.streetDot?.classList.toggle('show', on);
        this.cafeDot?.classList.toggle('show', on);
    }

    /** 出口钮按当前屏分派：地图屏=在家休息（+10 体力）；街景/影视城=离开即收口本周末 */
    private onFooter(): void {
        if (this.screen === 'map') {
            freeroamSystem.weekendStayHome();
            this.finish(true);
            return;
        }
        this.finish(false);
    }

    // ===== 前往（花 1 行动点） =====

    /**
     * 点地点直达（介绍抽屉与「前往·1行动点」按钮已删）。
     * 先问系统「今天这儿有内容吗」，有内容才进入；VN 播放中不许再点。
     * 扣点规则（2026-10-13 茄子令）：公园/影视城=进地扣 1 点；商业街=进街⛔ 扣点，进店才扣（enterShop）。
     */
    private tryGo(loc: LocationId): void {
        if (this.vn.open) return;
        const can = freeroamSystem.availability(loc);
        if (!can.ready) { showHint(can.reason ?? '当前无法前往'); return; }
        if (loc !== 'loc_street') {
            freeroamSystem.spendPoint();
            this.updateFooter();
        }
        if (loc === 'loc_park') { this.doPark(); return; }
        if (loc === 'loc_filmcity') { this.showScreen('filmcity'); return; }
        if (loc === 'loc_street') { this.showScreen('street'); return; }
    }

    // ===== 城市公园：偶遇掷点（2026-10-28 茄子令改 50%：PARK_MEET_PCT 中=遇人，没中=散步休息档） =====

    private doPark(): void {
        const plan = encounterSystem.rollPark();
        if (plan.kind === 'rest' || plan.kind === 'stroll') { this.playParkRest(); return; }
        if (plan.kind === 'special') {
            // 未结识特殊 NPC：公园初遇是剧情事件（候选已按 trigger.location+时间窗筛过），所以入队，随后当场演
            PlotSystem.getInstance().fireByEvent(plan.eventId);
            if (!this.playPlotBeat('scenes/bg-park.webp', 'map')) {
                // 演不成（载体不接／表内查无，交付侧已 warn 点过名），所以播散步休息档。
                // 不许再用「聊了几句近况」那种句子遮一段玩家根本没看到的剧情（2026-10-13 形状 C 收口）
                this.playParkRest();
            }
            return;
        }
        this.playParkEncounter(plan.offer);
    }

    /** 公园散步休息档（50% 未掷中、或池抽空/演不成的兜底）：一句散步文案 + 体力 +PARK_REST_ENERGY */
    private playParkRest(): void {
        const rest = freeroamSystem.parkRest();
        this.playVn(
            rest.lines.map(l => ({ speaker: NARRATOR, text: l.text, ...(l.changes ? { changes: l.changes } : {}) })) as DialogLine[],
            'scenes/bg-park.webp',
        );
    }

    /**
     * 地图侧的剧情演出（2026-10-13 形状 C 收口；2026-10-14 排干逻辑抽进 `plotPlayback.ts`，
     * 与月度执行页、决策页同吃一份）：本方法只负责记住「这一场垫哪张底图、收口回街景还是回地图」，
     * 再交给执行宿主的播放器。场地口径见 `FixedEventDef.playOn`（播出侧那类已挪去决策页，这里拿不到）。
     */
    private playPlotBeat(scene: string, landing: 'street' | 'map'): boolean {
        this.plotScene = scene;
        this.plotLanding = landing;
        return this.plotPlayer.playNext();
    }

    /** MediaPanel「知道了」回调：推进剧情队列，接着演下一条或收场回地图 */
    private onMediaDone(): void {
        this.plotPlayer.onMediaDone();
    }

    /**
     * 熟遇/探班公共 VN 播放（2026-10-15 抽取）：offer.lines + 选项 + onPick 结算续播。
     * 差异由参数注入：scene 背景图、resolver 结算函数、narratorLine 可选旁白前缀。
     */
    private playOfferWithChoices(
        offer: EncounterOffer,
        speaker: DialogSpeaker,
        scene: string,
        resolver: (offer: EncounterOffer, idx: number) => EncounterOutcome,
        narratorLine?: string,
        landing: 'street' | 'map' = 'map',
    ): void {
        const build = (): DialogLine[] => {
            const lines: DialogLine[] = [];
            if (narratorLine) lines.push({ speaker: NARRATOR, text: narratorLine });
            offer.lines.forEach((text, i) => {
                if (i < offer.lines.length - 1) { lines.push({ speaker, text }); return; }
                lines.push({
                    speaker,
                    text,
                    choices: offer.choices.map(c => ({
                        text: c.text,
                        ...(typeof c.prob === 'number' ? { prob: c.prob } : {}),
                        ...(c.reply ? { reply: { speaker, text: c.reply, ...(c.changes ? { changes: c.changes } : {}) } } : {}),
                    })),
                    onPick: (idx: number) => {
                        const outcome = resolver(offer, idx);
                        // 不落盘（月存口径 2026-11-03）：偶遇结算等月初那一格捎带；月内退出=重玩本月（偶遇重掷）
                        const choice = offer.choices[idx];
                        if (outcome.reply) {
                            this.vn.play(
                                [{ speaker, text: outcome.reply, ...(outcome.changes ? { changes: outcome.changes } : {}) } as DialogLine],
                                () => this.endVisit(landing),
                            );
                        } else if (!choice.reply) {
                            this.endVisit(landing);
                        }
                    },
                });
            });
            return lines;
        };
        this.showSceneBg(scene);
        this.sceneEl.classList.add('show');
        withSpeakerAvatar(speaker, () => this.playVn(build()));
    }

    /** 公园偶遇 VN */
    private playParkEncounter(offer: EncounterOffer): void {
        if (offer.unreachable) {
            this.playVn(
                offer.lines.map(text => ({ speaker: NARRATOR, text, changes: [{ label: '', delta: UNREACHABLE_LINES.hint }] })),
                'scenes/bg-park.webp',
            );
            return;
        }
        const speaker: DialogSpeaker = { id: offer.npcId, name: offer.npcName, avatarChar: offer.npcName.slice(0, 1) };
        this.playOfferWithChoices(offer, speaker, 'scenes/bg-park.webp', (o, i) => encounterSystem.resolve(o, i));
    }

    // ===== 环亚影视城：先选剧组，再选组里见谁（2026-10-12 茄子令） =====

    private renderFilmList(): void {
        const list = this.filmWrap.querySelector<HTMLElement>('[data-role=filmList]')!;
        list.innerHTML = '';
        const crews = freeroamSystem.filmcityCrews();
        if (crews.length === 0) {
            list.appendChild(createDiv('fr-film-empty', [freeroamSystem.filmcityEmptyText()]));
            return;
        }
        crews.forEach(crew => {
            const script = GameData.SCRIPT_MAP[crew.scriptId];
            const open = this.openCrew === crew.scriptId;
            const row = createButton('fr-film-row', '');
            if (open) row.classList.add('open');
            else if (crew.known.length === 0) row.classList.add('blocked');
            const top = createDiv('fr-film-top');
            top.appendChild(createSpan('fr-film-drama', script ? `《${script.name}》` : '无名剧组'));
            top.appendChild(createSpan('fr-film-work', '拍摄中'));
            row.appendChild(top);
            // 名字同一时刻只出现在一处：收起态在第二行，展开态由子行接管（茄子令：展开后不许再留一行提示字）
            if (!open) {
                row.appendChild(createDiv('fr-film-known', [crew.known.length > 0
                    ? `组里认识的：${crew.known.map(m => m.name).join('、')}` : '组里没有你认识的人']));
            }
            row.addEventListener('click', e => {
                e.stopPropagation();
                // 置灰但点得动（10-04 第六令配方）：点按只报一句，不许扣点、不许进二级
                if (crew.known.length === 0) { showHint('组内没有相识的人，无法探班'); return; }
                this.openCrew = open ? null : crew.scriptId;
                this.renderFilmList();
            });
            list.appendChild(row);
            if (!open) return;
            const box = createDiv('fr-film-members');
            crew.known.forEach(m => {
                const role = script?.roles[m.roleIndex];
                const spent = !freeroamSystem.canVisit(m.npcId);
                const item = createButton('fr-film-member', '', () => {
                    this.doVisit(m.npcId, crew.scriptId);
                });
                // 本月那次已经探过 ⇒ 压灰加标记，但仍点得动（只报一句，与父行灰组同形制；
                // 探班月闸 2026-10-12 茄子令「一个月探班同一个人最多一次」）
                if (spent) item.classList.add('spent');
                item.appendChild(createSpan('fr-film-name', m.name));
                // 顺序＝名字 → 已探望标记 → 角色名：角色名在子行右缘是不变量（有无标记都不许把它挤到中间）
                if (spent) item.appendChild(createSpan('fr-film-spent', '本月已探望'));
                item.appendChild(createSpan('fr-film-role', role ? (role.name ? `饰 ${role.name}` : role.roleType) : ''));
                box.appendChild(item);
            });
            list.appendChild(box);
        });
    }

    /**
     * 探班演出（2026-10-15）：委托 playOfferWithChoices，差异=片场背景 + resolveVisit + 旁白前缀。
     */
    private doVisit(npcId: string, scriptId: string): void {
        const res = freeroamSystem.visit(npcId);
        if (!res.ok) { showHint('本月已探班过该人脉'); return; }
        const offer = res.offer;
        const speaker: DialogSpeaker = { id: offer.npcId, name: offer.npcName, avatarChar: offer.npcName.slice(0, 1) };
        const scene = scriptSystem.isCostumeScript(scriptId) ? 'scenes/bg-filmset-costume.webp' : 'scenes/bg-filmset-modern.webp';
        this.playOfferWithChoices(
            offer, speaker, scene,
            (o, i) => encounterSystem.resolveVisit(o, i),
            `片场休息区，${offer.npcName} 卸了妆过来打招呼。`,
        );
    }

    // ===== 拾光咖啡馆：进店菜单（随便坐坐 / 兼职） =====

    /**
     * 拾光咖啡馆进店菜单（2026-10-28 茄子令「兼职改成可选」）：一句话两选项——
     * 随便坐坐=掷点（CAFE_LOUNGE_MEET_PCT 中=偶遇，池与公园完全同款 rollCafe；没中=独坐档）；
     * 兼职=金钱 +CAFE_JOB_PAY。特殊 NPC 初遇已在 enterShop 那一步优先消费（到访才触发），走不到这里。
     */
    private doCafe(): void {
        this.playVn(
            [{
                speaker: NARRATOR,
                text: '来拾光咖啡馆做什么？',
                choices: [{ text: '随便坐坐' }, { text: '兼职' }],
                onPick: (idx: number) => this.onCafeMenuPick(idx),
            } as DialogLine],
            'scenes/bg-cafe.webp',
            () => this.endVisit('street'),   // 咖啡馆在街景里：还剩点回街（可进另一家），没点收口进下周
        );
    }

    /** 菜单分支：结算文案读完统一回街景（还剩点可进另一家，没点收口进下周） */
    private onCafeMenuPick(idx: number): void {
        if (idx === 1) {
            const job = freeroamSystem.workAtCafe();
            this.playVn(
                job.lines.map(l => ({ speaker: NARRATOR, text: l.text, ...(l.changes ? { changes: l.changes } : {}) })) as DialogLine[],
                'scenes/bg-cafe.webp',
                () => this.endVisit('street'),
            );
            return;
        }
        const roll = freeroamSystem.cafeLoungeRoll();
        if (roll.kind === 'sit') { this.playCafeSit(); return; }
        if (roll.plan.kind === 'stroll' || roll.plan.kind === 'rest') { this.playCafeSit(); return; }   // 池抽空＝独坐档兜底（rollCafe 不出 rest，类型上兜住）
        if (roll.plan.kind === 'special') {
            // 理论不可达（rollCafe 不给未结识特殊 NPC 开口，见 EncounterSystem.rollCafe 头注）；兜底照公园形制
            PlotSystem.getInstance().fireByEvent(roll.plan.eventId);
            if (!this.playPlotBeat('scenes/bg-cafe.webp', 'street')) this.playCafeSit();
            return;
        }
        this.playCafeEncounter(roll.plan.offer);
    }

    /** 独坐档（掷中休息、或偶遇池抽空时的兜底） */
    private playCafeSit(): void {
        const sit = freeroamSystem.cafeSit();
        this.playVn(
            sit.lines.map(l => ({ speaker: NARRATOR, text: l.text, ...(l.changes ? { changes: l.changes } : {}) })) as DialogLine[],
            'scenes/bg-cafe.webp',
            () => this.endVisit('street'),
        );
    }

    /** 咖啡馆偶遇 VN（初遇/熟遇与公园同一套 offer 机制，底图与落点换咖啡馆侧） */
    private playCafeEncounter(offer: EncounterOffer): void {
        if (offer.unreachable) {
            this.playVn(
                offer.lines.map(text => ({ speaker: NARRATOR, text, changes: [{ label: '', delta: UNREACHABLE_LINES.hint }] })),
                'scenes/bg-cafe.webp',
            );
            return;
        }
        const speaker: DialogSpeaker = { id: offer.npcId, name: offer.npcName, avatarChar: offer.npcName.slice(0, 1) };
        this.playOfferWithChoices(offer, speaker, 'scenes/bg-cafe.webp', (o, i) => encounterSystem.resolve(o, i), undefined, 'street');
    }

    // ===== 商业街六店 =====

    /**
     * 点店招开店面：咖啡馆=兼职（走 doCafe），礼品店与四家部件店各一组件（懒建）。
     * 扣点规则（2026-10-13 茄子令）：进街⛔ 扣点，**进店才扣 1 点**；离店/播完收口——
     * 还剩点=回街景（可进另一家），没点=直接推进下一周。
     */
    private enterShop(id: LocationId): void {
        if (this.vn.open) return;   // 同上：VN 播放中锁店内交互
        if (id === 'shop_cafe') {
            if (!freeroamSystem.canSpend()) { showHint('本周末行动点已用完'); return; }
            freeroamSystem.spendPoint();
            this.updateFooter();
            // 特殊 NPC 初遇优先（meet.events 候选里带 location=shop_cafe 且时间窗满足的，如许林辉 FIXED_901_MEET）：
            // 演不成（载体不接／派发被拒）就回落到进店菜单，扣掉的点不退——玩家确实进了店
            const meet = PlotSystem.getInstance().meetEventAtLocation(id);
            if (meet && PlotSystem.getInstance().fireByEvent(meet.eventId)
                && this.playPlotBeat('scenes/bg-cafe.webp', 'street')) return;
            this.doCafe();
            return;
        }
        if (!freeroamSystem.canSpend()) { showHint('本周末行动点已用完'); return; }
        freeroamSystem.spendPoint();
        this.updateFooter();
        const shop = this.shopOf(id);
        if (!shop) return;
        // 店面挂面板根（不许留在覆盖层内）：覆盖层顶到 `--exec-top` 且 overflow:hidden 会裁子层，
        // 店面待在层内就盖不住信息栏 ⇒ 顶栏一行金钱 + 店头一行金钱叠成两个读数（z 见 ui.css `.fr-shop-full`）
        const host = this.element.parentElement ?? this.element;
        if (shop.element.parentElement !== host) host.appendChild(shop.element);
        this.openShop = shop;
        // 离店=收口判定（2026-10-13 茄子令）：还剩点回街景（可进另一家），没点直接推进下一周
        shop.open(
            () => this.refreshHost(),
            (hidden) => this.setHudHidden(hidden),
            () => { this.closeShop(); if (freeroamSystem.pointsLeft > 0) this.showScreen('street'); else this.finish(false); },
        );
        this.updateFooter();
    }

    /** 店 → 组件（一次周末通常只进一两家 ⇒ 懒建；同一时刻只开一家，换店先 closeShop） */
    private shopOf(id: LocationId): ShopHandle | null {
        if (id === 'shop_gift') return this.giftShop ??= new GiftShop();
        const catKeys = SHOP_CATS[id];
        if (!catKeys) return null;
        return this.partShops[id] ??= new PartShop(locName(id), catKeys);
    }

    /** 收店面：组件自己摘 .show 之外，宿主还要恢复信息栏层与出口钮（离开/换屏/收口共用此口） */
    private closeShop(): void {
        this.openShop?.close();
        this.openShop = null;
        this.setHudHidden(false);
        this.updateFooter();
    }

    /** 店面全屏时把信息栏/周点条压到店面之下（fr-outing 的 z 档只属于地图态） */
    private setHudHidden(hidden: boolean): void {
        this.element.parentElement?.classList.toggle('fr-inshop', hidden);
    }

    // ===== VN（组件自带，覆盖层内演出） =====

    /**
     * 地点场景层底图：先铺 LQIP 占位，图加载完成再淡入（与三屏 bgwrap 同口径，2026-10-29 §3.4cm 四轮）。
     * 失败时 LQIP 常驻兜底，不出破图。
     */
    private showSceneBg(url: string): void {
        applyLqip(this.sceneEl, url);
        this.sceneImg.style.opacity = '0';
        let settled = false;
        const reveal = (): void => {
            if (settled) return;
            settled = true;
            requestAnimationFrame(() => { this.sceneImg.style.opacity = '1'; });
            setTimeout(() => clearLqip(this.sceneEl), 280);
        };
        this.sceneImg.onload = reveal;
        this.sceneImg.onerror = () => { /* 失败：LQIP 常驻兜底 */ };
        this.sceneImg.src = url;
        if (this.sceneImg.complete && this.sceneImg.naturalWidth) reveal();
    }

    /**
     * VN 演出（覆盖层自带卡）：scene 给地点场景图（VN 期间全屏垫底，读毕收层）。
     * ⚠️ stayOpen 组件读毕不会自收——宿主叙事卡常驻是对的，地图这里必须 stop() 收卡，
     * 否则最后一句点掉后对话框一直浮在地图上（实机首夜抓到）。
     */
    private playVn(lines: DialogLine[], scene?: string, onEnd?: (picked: number | null) => void): void {
        if (scene) {
            this.showSceneBg(scene);
            this.sceneEl.classList.add('show');
        }
        this.vn.stayOpen = true;
        // 缺省收口＝endVisit；多拍剧情传 onEnd 自己接着演下一拍（2026-10-13 形状 C 演出通道）
        this.vn.play(lines, picked => (onEnd ? onEnd(picked) : this.endVisit()));
        this.updateFooter();   // 演出期收钮（读毕走 endVisit→showScreen 再出）
    }

    /**
     * 演出收口：收场景层、收 VN 卡，然后按「这屏还有没有事做」决定落点。
     * 点数已花 ⇒ 地图也是死路（哪都进不去，回图后玩家还得再点一颗收口钮）⇒ 直接收掉整个周末
     * （2026-10-12 茄子令「行动点消耗完之后 没有必要回到地图了」）；还剩点才退回地图继续选
     * （若日后行动点升格成 N 处，这条判据自动把"回图"还给玩家）。
     */
    /**
     * 演出收口：收场景层、收 VN 卡，然后按「这屏还有没有事做」决定落点。
     * 落点按来源分（2026-10-13 进店才扣点）：商业街内的咖啡馆=回街景（还有点）或收口；
     * 公园/影视城=回地图（还有点）或收口。没点一律直接推进下一周（死地图不许晾人）。
     */
    private endVisit(landing: 'street' | 'map' = 'map'): void {
        this.sceneEl.classList.remove('show');
        this.vn.stop();
        if (freeroamSystem.pointsLeft > 0) this.showScreen(landing); else this.finish(false);
    }
}

/** 旁白态说话人（与 MonthProgressPanel 同形制：无头像无姓名=叙述行） */
const NARRATOR: DialogSpeaker = { id: 'narration', name: '', noAvatar: true };

function createImg(src: string, cls = 'fr-bg'): HTMLImageElement {
    const img = document.createElement('img');
    img.className = cls;
    img.alt = '';
    img.src = src;
    return img;
}

/**
 * 三屏全屏底图：先铺 LQIP 占位底，图加载完成后淡入并清占位（2026-10-29 §3.4cm 四轮）。
 * ⛔ 白屏一帧：`.fr-bg` 默认 opacity 0（ui.css），只有这里负责把它推到 1。
 */
function bindBgLqip(wrap: HTMLElement, img: HTMLImageElement, url: string): void {
    applyLqip(wrap, url);
    let settled = false;
    const reveal = (): void => {
        if (settled) return;
        settled = true;
        requestAnimationFrame(() => { img.style.opacity = '1'; });
        setTimeout(() => clearLqip(wrap), 280);
    };
    img.addEventListener('load', reveal, { once: true });
    if (img.complete && img.naturalWidth) reveal();
}

/** 待事圆点（无字；显隐由 `updatePendingDots` 统一收放，画法与判据的来龙去脉写在那一侧） */
function pendingDot(): HTMLSpanElement {
    return createSpan('fr-pending', '');
}

/** 地名一律读 `Locations.ts`（10-12 卫生：本文件原先另抄了一份九条名字表，改表必漂移） */
function locName(id: LocationId): string {
    return LOCATION_MAP[id].name;
}


